// #region Imports
import SaveIcon from "@mui/icons-material/Save";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import AvatarUpload from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import ProfileNameInput from "marketplace/_views/profiles/profile-name-input";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { TeamJSON } from "marketplace/teams/_schema/team-schema";
import {
  useCancelTeamInvitationMutation,
  useCreateTeamAvatarMutation,
  useCreateTeamInvitationMutation,
  useCreateTeamMutation,
  useGetTeamByIdQuery,
  useGetTeamInvitationsByTeamIdQuery,
  usePromoteTeamMemberToLeaderMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMutation,
} from "marketplace/teams/_api/marketplace-team-api";
import {
  getTeamAvatar,
  getTeamById,
  getTeamInvitationsByTeamId,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import TeamInvitation from "marketplace/teams/invitations/team-invitation";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction, SetURLSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import TeamProfile from "../team-profile";
import EditTeam from "./edit-team";
import { skipToken } from "@reduxjs/toolkit/query";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "grid",
  gridTemplateRows: "repeat(5, auto)",
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridRow: "1",
  justifyContent: "space-between",
  padding: theme.spacing(0, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const AboutYou = styled("section")(({ theme }) => ({
  gridRow: "3",
  marginTop: theme.spacing(1),
}));
const AboutYouTextField = styled(TextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  flex: 1,
  width: "100%",
}));
const RegisterToMarketplaceCheckbox = styled(FormControlLabel)(() => ({
  whiteSpace: "nowrap",
}));
const TeamDetails = styled("section")(({ theme }) => ({
  gridRow: "4",
  marginTop: theme.spacing(1),
}));
const EditTeamMembers = styled(EditTeam)(() => ({
  gridRow: "2",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: 0,
  gridRow: "5",
  justifySelf: "flex-start",
  padding: theme.spacing(2, 0),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
// #endregion

// #region Component Props
export type EditTeamProfileProps = {
  isVisible?: boolean;
  teamId?: Guid;
  profile?: TeamProfile;
  navigate: NavigateFunction;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (team: Team) => void;
};
// #endregion

export default function EditTeamProfile(props: Readonly<EditTeamProfileProps>) {
  const { navigate, searchParams, setSearchParams, onFormDirty, onSave } = props;

  // #region State
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isProfileValidated, setIsProfileValidated] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);

  const [teamId, setTeamId] = useState<Guid | undefined>(props.teamId);
  const [profile, setProfile] = useState<TeamProfile | undefined>(props.profile);
  const [updatedAvatar, setUpdatedAvatar] = useState<File | string | undefined | null>();

  const [draftTeamName, setDraftTeamName] = useState<string | null>(null);
  const [formTeamName, setFormTeamName] = useState<string>(props.profile?.name ?? Config.defaultProfileName);
  const [formCategories, setFormCategories] = useState<string[]>(props.profile?.categories ?? []);
  const [formDescription, setFormDescription] = useState<string>(props.profile?.description ?? "");
  const [formRegisterToMarketplace, setFormRegisterToMarketplace] = useState<boolean>(isVisible ?? true);
  const [originalRegisterToMarketplace, setOriginalRegisterToMarketplace] = useState<boolean>(isVisible ?? true);
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<Individual>();
  const [promotingMemberToLeader, setPromotingMemberToLeader] = useState<boolean>(false);
  const [existingTeamMembers, setExistingTeamMembers] = useState<Individual[]>([]);
  const [invitedMembers, setInvitedMembers] = useState<Individual[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [removedMemberUserIds, setRemovedMemberUserIds] = useState<Guid[]>([]);
  const [profileHash, setProfileHash] = useState<string>("");

  // #region Hooks
  const session = useSession();

  const [updateTeam] = useUpdateTeamMutation();
  const [createTeam] = useCreateTeamMutation();
  const [createTeamAvatar] = useCreateTeamAvatarMutation();
  const [removeTeamMember] = useRemoveTeamMemberMutation();
  const [promoteTeamMember] = usePromoteTeamMemberToLeaderMutation();
  const [cancelTeamInvitation] = useCancelTeamInvitationMutation();
  const [createTeamInvitation] = useCreateTeamInvitationMutation();

  const teamQuery = useGetTeamByIdQuery(teamId ? { teamId: teamId.value } : { teamId: "" }, { skip: !teamId });
  const invitationsQuery = useGetTeamInvitationsByTeamIdQuery(
    teamId ? { teamId: teamId.value, open: true } : skipToken,
    { skip: !teamId },
  );

  // #region Selectors
  const team = useSelector(getTeamById(teamId));
  const { isLoading: isLoadingTeam, error: errorLoadingTeam } = teamQuery;
  const teamAvatar = useSelector(getTeamAvatar(teamId ?? null));
  const teamInvitations = useSelector(getTeamInvitationsByTeamId(teamId));
  const { isLoading: isLoadingTeamInvitations, error: errorLoadingTeamInvitations } = invitationsQuery;

  useEffect(() => {
    if (!teamId || teamInvitations || isLoadingTeamInvitations || errorLoadingTeamInvitations) {
      return;
    }
    // Invitations loaded via useGetTeamInvitationsByTeamIdQuery above
  }, [errorLoadingTeamInvitations, isLoadingTeamInvitations, teamId, teamInvitations]);


  
  const refreshIsDirty = useCallback((): boolean => {
    if (!profile) {
      onFormDirty?.(false);
      return false;
    }

    // Compare original register to marketplace to updated register to marketplace
    if (formRegisterToMarketplace !== originalRegisterToMarketplace) {
      onFormDirty?.(true);
      return true;
    }

    if (removedMemberUserIds.length > 0) {
      onFormDirty?.(true);
      return true;
    }

    if (invitedMembers.length > 0) {
      onFormDirty?.(true);
      return true;
    }

    // Compare profile hash to updated vendor-team profile hash
    const hash = JSON.stringify(profile.toJSON());
    const isDirty = hash !== profileHash;
    onFormDirty?.(isDirty);
    return isDirty;
  }, [formRegisterToMarketplace, originalRegisterToMarketplace, profile, profileHash, invitedMembers, removedMemberUserIds, onFormDirty]);


  // #region Validation
  const validateProfile = useCallback((profile: TeamProfile) => {
    if (!profile) return;
    const hasCategories: boolean = profile.categories !== undefined && profile.categories.length > 0;
    const hasName: boolean = profile.name.length > 0 && profile.name !== Config.defaultProfileName;
    setIsProfileValidated(hasName && hasCategories);
    refreshIsDirty();
  }, [refreshIsDirty]);

  const constructNewTeamProfile = useCallback((): TeamProfile => {
    const individualId = session.user!.individualId;
    const legalEntityId = session.user!.legalEntityId;
    const userId = session.user!.id;
    const firstName = session.user?.name?.firstName;
    const lastName = session.user?.name?.lastName;

    if (!individualId) throw new Error("Individual not found");
    if (!legalEntityId) throw new Error("Entity not found");
    if (!userId) throw new Error("User not found");
    if (!firstName) throw new Error("Individual first name not found");
    if (!lastName) throw new Error("Individual last name not found");

    const leader = Individual.create({
      id: individualId,
      userId,
      legalEntityId,
      profile: IndividualProfile.create({ userId, firstName, lastName }),
      isVisible: true,
    });

    setSelectedTeamLeader(leader);
    return new TeamProfile({
      name: formTeamName,
      categories: formCategories,
      description: formDescription,
    });
  }, [formCategories, formDescription, formTeamName, session.user]);


  // #region Lifecycle
  useEffect(() => {
    if (!team && !teamId && !profile) {
      setProfile(constructNewTeamProfile());
      return;
    }

    // No team/profile
    if (!team?.profile) return;

    // Nothing changed
    if (profileHash === JSON.stringify(team.profile?.toJSON())) return;


    
    // Populate form with team profile data
    setProfile(team.profile);
    setIsVisible(team.isVisible ?? false);
    setFormTeamName(team.profile.name);
    setSelectedTeamLeader(team.leader);
    setExistingTeamMembers(team.members);
    setFormCategories(team.profile.categories ?? []);
    setFormDescription(team.profile.description ?? "");

    validateProfile(team.profile);

    setProfileHash(JSON.stringify(team.profile?.toJSON()));

    if (team.isVisible !== undefined) {
      setFormRegisterToMarketplace(team.isVisible);
      setOriginalRegisterToMarketplace(team.isVisible);
    }
  }, [team, teamId, profileHash, profile, validateProfile, constructNewTeamProfile]);

  useEffect(() => {
    if (errorLoadingTeam) {
      enqueueSnackbar("Profile currently unavailable", { variant: "warning" });
    }
  }, [errorLoadingTeam]);
  // #endregion

  useEffect(() => {
    if (!profile) return;
    validateProfile(profile);
  }, [profile, validateProfile]);

  // #region Functions

  function constructTeam(): Team {
    if (!profile) throw new Error("Updated Team profile not found");
    if (!selectedTeamLeader) throw new Error("Team leader not found");

    return new Team({
      leader: selectedTeamLeader,
      profile: profile,
      isVisible: formRegisterToMarketplace,
      associatedFirms: [],
      members: existingTeamMembers,
      id: teamId ?? Guid.generate(),
    });
  }

  async function handleSaveButtonClick() {
    let updatedTeam: Team | undefined;

    try {
      setIsSaving(true);

      if (teamId) {
        await removeMembers(teamId);
        await inviteMembers(teamId);
        if (promotingMemberToLeader) await promoteMemberToLeader(teamId);
        updatedTeam = await updateProfile();
      } else {
        updatedTeam = await createProfile();
      }
      if (!updatedTeam.id) throw new Error("Failed to save profile");

      setInvitedMembers([]);
      setPendingInvitations([]);
      setRemovedMemberUserIds([]);

      setTeamId(updatedTeam.id);
      setFormRegisterToMarketplace(updatedTeam.isVisible ?? false);
      setOriginalRegisterToMarketplace(updatedTeam.isVisible ?? false);
      setProfileHash(JSON.stringify(updatedTeam.profile.toJSON()));

      enqueueSnackbar("Profile changes saved", { variant: "success" });
      onSave?.(updatedTeam);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to save profile", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  async function updateProfile(): Promise<Team> {
    try {
      if (!isProfileValidated) throw new Error("Profile is not valid");
      if (!team) throw new Error("Team not found");
      if (!profile) throw new Error("Updated vendor-team profile not found");

      const updatedTeam: Team = constructTeam();
      if (updatedAvatar && updatedAvatar instanceof File) {
        await createAvatar(updatedTeam.id, updatedAvatar);
      }
      const teamResponseJson: TeamJSON = await updateTeam({ teamId: updatedTeam.id.value, originalTeam: team, updatedTeam }).unwrap();
      if (!teamResponseJson.id) throw new Error("Unable to update vendor-team");

      const teamResponse = Team.fromJSON(teamResponseJson);
      setProfile(teamResponse.profile);
      return teamResponse;
    } catch (error) {
      console.error(error);
      throw new Error("Unable to update profile");
    }
  }

  async function createProfile(): Promise<Team> {
    try {
      if (!isProfileValidated) throw new Error("Profile is not valid");

      const teamToCreate: Team = constructTeam();
      const createdTeamJson: TeamJSON = await createTeam({ team: teamToCreate }).unwrap();

      if (!createdTeamJson.id) throw new Error("Unable to create vendor-team");

      const createdTeam = Team.fromJSON(createdTeamJson);
      setProfile(createdTeam.profile);
      if (updatedAvatar) await createAvatar(createdTeam.id, updatedAvatar as File);

      return createdTeam;
    } catch (error) {
      console.error(error);
      throw new Error("Unable to create profile");
    }
  }

  async function createAvatar(teamId: Guid, avatar: File) {
    if (!teamId || !avatar) return;

    try {
      return await createTeamAvatar({ teamId: teamId.value, file: avatar }).unwrap();
    } catch (error) {
      console.error(error);
      throw new Error("Unable to create avatar");
    }
  }

  async function removeMembers(teamId: Guid) {
    for (const userId of removedMemberUserIds) {
      try {
        const pendingInvite = pendingInvitations.find((invite) => invite.inviteeId.isEqualTo(userId));

        if (pendingInvite) {
          await cancelTeamInvitation({ invitationId: pendingInvite.id.value });
          continue;
        }
        await removeTeamMember({ teamId: teamId.value, memberIndividualId: userId.value });
      } catch (error) {
        console.error(error);
        throw new Error(`Unable to remove member with userId ${userId}`);
      }
    }
  }

  async function inviteMembers(teamId: Guid) {
    for (const invitation of pendingInvitations) {
      await createTeamInvitation({
        inviteeId: invitation.inviteeId.value,
        teamId: teamId.value,
        message: invitation.message,
      });
    }
  }

  async function promoteMemberToLeader(teamId: Guid) {
    const memberToPromote = team?.members.find((member) => member.userId?.isEqualTo(team.leader.userId));

    if (memberToPromote && memberToPromote.userId) {
      await promoteTeamMember({ teamId: teamId.value, individualId: memberToPromote.userId.value });
    }

    setPromotingMemberToLeader(false);
  }

  function handleMemberAdded(newMember: Individual) {
    if (!team) throw new Error("Team not found");
    if (invitedMembers.some((member) => member?.isEqualTo(newMember))) return;
    setInvitedMembers([...invitedMembers, newMember]);
    setRemovedMemberUserIds(removedMemberUserIds.filter((id) => !id.isEqualTo(newMember.userId)));
    setPendingInvitations([
      ...pendingInvitations,
      TeamInvitation.create({
        id: Guid.generate(),
        inviteeId: newMember.id,
        teamId: team.id,
        message: "",
        closed: false,
      }),
    ]);
  }

  function handleInvitationUpdated(updatedInvitation: TeamInvitation) {
    const updatedInvitations = pendingInvitations.map((pendingInvitation) => {
      if (pendingInvitation.id.isEqualTo(updatedInvitation.id)) {
        return updatedInvitation;
      }
      return pendingInvitation;
    });
    setPendingInvitations(updatedInvitations);
  }

  function handleMemberRemoved(individual: Individual) {
    setRemovedMemberUserIds([...removedMemberUserIds, individual.userId]);
    setInvitedMembers(invitedMembers.filter((member) => !member.isEqualTo(individual)));
  }
  // #endregion

  // #region Render
  return (
    <MainContainer>
      <HeaderContainer>
        <AvatarNameContainer>
          <AvatarUpload
            accept="image/jpeg, image/png"
            avatar={updatedAvatar ?? teamAvatar}
            onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
          />
          <ProfileNameInput
            name={formTeamName}
            draftName={draftTeamName ?? undefined}
            isLoading={isLoadingTeam}
            error={(formTeamName === Config.defaultProfileName && teamId && !isLoadingTeam) || !!errorLoadingTeam}
            onStartEditing={() => setDraftTeamName(formTeamName)}
            onDraftChange={(draft: string) => setDraftTeamName(draft)}
            onCommit={(finalName: string) => {
              setFormTeamName(finalName);
              if (!profile) return;
              const newProfile = new TeamProfile({
                ...profile,
                name: finalName,
              });
              setProfile(newProfile);
              validateProfile(newProfile);
              setDraftTeamName(null);
            }}
            onCancel={() => setDraftTeamName(null)}
          />
        </AvatarNameContainer>
        <ProfileCategoriesSelect
          options={Config.CompanyProfileTypes}
          categories={formCategories}
          onChange={async (updatedCategories: string[]) => {
            setFormCategories(updatedCategories);
            if (!profile) return;
            const newProfile = new TeamProfile({
              ...profile,
              categories: updatedCategories,
            });
            setProfile(newProfile);
            validateProfile(newProfile);
            setDraftTeamName(null);
          }}
        />
      </HeaderContainer>
      {isLoadingTeam && <Loader />}
      <>
        {!isLoadingTeam && team && (
          <EditTeamMembers
            navigate={navigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            team={team}
            invitations={teamInvitations}
            removedUserIds={removedMemberUserIds}
            onMemberAdded={handleMemberAdded}
            onInvitationUpdated={handleInvitationUpdated}
            onMemberRemoved={handleMemberRemoved}
          />
        )}

        <AboutYou>
          <Typography variant="h6">About Your Team</Typography>
          <AboutYouTextField
            value={formDescription}
            onChange={(event) => {
              setFormDescription(event.target.value);
              if (!profile) return;
              const newProfile = new TeamProfile({
                ...profile,
                description: event.target.value,
              });
              setProfile(newProfile);
              validateProfile(newProfile);
              setDraftTeamName(null);
            }}
            placeholder="Additional info displayed on your profile"
            variant="outlined"
            multiline
          />
        </AboutYou>
        <TeamDetails>
          <RegisterToMarketplaceCheckbox
            label="Show Team On Marketplace"
            labelPlacement="end"
            control={
              <Checkbox
                color="primary"
                checked={formRegisterToMarketplace}
                onChange={(event) => {
                  setFormRegisterToMarketplace(event.target.checked);
                  onFormDirty?.(true);
                }}
              />
            }
          />
        </TeamDetails>
      </>
      <ButtonContainer>
        <LoadingButton
          id="saveButton"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!isProfileValidated || isSaving}
          loading={isSaving}
          color="primary"
          onClick={handleSaveButtonClick}
        >
          {teamId ? "Save Changes" : "Create Profile"}
        </LoadingButton>
      </ButtonContainer>
    </MainContainer>
  );
}
// #endregion
